/**
 * HygieneCard — the card component in its hygiene variant (roadmap v1.4.2 R1, U5).
 *
 *   fact · action · [Listo] [No me interesa] · verified read-back
 *
 * One ficha a week from the audit's mechanical findings, paced by goals-handler/hygiene.js.
 * The merchant sees WHAT is broken (items with admin links), taps Listo when done, and the
 * next weekly pass verifies it against the catalog ("Listo — ya no hay …"). Manual-verify
 * fichas (heavy images) close on the merchant's word and say so.
 *
 * States rendered:
 *   proposed (served)       → fact + items + [Listo] [No me interesa]
 *   active, unverified      → "Marcado como hecho. Se confirma en la próxima revisión…"
 *   active, remaining > 0   → "Todavía quedan N pendientes" + the remaining items
 *   done (catalog)          → Hecho · "Confirmado en tu catálogo el …"
 *   done (merchant)         → Hecho · "Marcado como hecho. Este cambio no se verifica automáticamente."
 *   done (probe)            → Hecho · "Confirmado: las páginas vuelven a responder …" (NAV-09)
 *   done (traffic_stopped)  → Cerrado · the pages stopped receiving visits — said as such, not as a fix
 *
 * NAV-09 items are pages (path + full URL in dato.url); their fix is confirmed by requesting
 * the page in the weekly pass (Mondays), not by the catalog.
 *
 * Copy: rioplatense voseo, no jargon, never a check id. IMPERSONAL VOICE (user-set,
 * sept-15): the app describes state — "se confirma", "marcado como hecho", "no hay visitas
 * registradas" — never "vemos", "lo dimos por hecho", "no podemos".
 */
import { useState } from 'react'
import { Card, Text, Tag, Button } from '@nimbus-ds/components'
import { goalService, type Actionable } from '@/api/goals'
import { trackAppEvent } from '@/lib/appTelemetry'

const ITEM_LIMIT = 6

const ENTIDAD_LABEL: Record<string, { one: string; many: string }> = {
  product: { one: 'producto', many: 'productos' },
  image: { one: 'imagen', many: 'imágenes' },
  category: { one: 'categoría', many: 'categorías' },
  link: { one: 'link', many: 'links' },
}

function itemsWord(entidad: string | undefined, n: number) {
  const e = ENTIDAD_LABEL[entidad || 'product'] || ENTIDAD_LABEL.product
  return n === 1 ? e.one : e.many
}

function datoLine(dato?: Record<string, unknown>): string | null {
  if (!dato) return null
  if (typeof dato.visitas_7d === 'number') {
    // NAV-09: a page that no longer exists and still receives campaign visits.
    const parts = [
      typeof dato.problema === 'string' ? dato.problema : null,
      `${dato.visitas_7d} visita${dato.visitas_7d === 1 ? '' : 's'} en ${typeof dato.dias === 'number' ? dato.dias : 7} días`,
      typeof dato.campana === 'string' ? `campaña ${dato.campana}` : null,
      typeof dato.categoria_sugerida === 'string' ? `categoría sugerida: ${dato.categoria_sugerida}` : null,
    ]
    return parts.filter(Boolean).join(' · ')
  }
  if (typeof dato.kb === 'number') return `${Math.round(dato.kb)} KB`
  if (typeof dato.problema === 'string') return dato.problema
  if (typeof dato.http === 'number') return `error ${dato.http}`
  return null
}

export function HygieneCard({ actionable, onChange }: { actionable: Actionable; onChange?: (a: Actionable) => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)
  const h = actionable.hygiene
  if (!h) return null

  const remainingIds = h.remaining_ids && h.remaining_ids.length ? new Set(h.remaining_ids) : null
  const items = remainingIds ? h.items.filter((i) => remainingIds.has(i.id)) : h.items
  const shown = showAll ? items : items.slice(0, ITEM_LIMIT)
  const adminUrl = (dato?: Record<string, unknown>) =>
    typeof dato?.admin_url === 'string' ? (dato.admin_url as string) : typeof dato?.url === 'string' ? (dato.url as string) : null
  const byProbe = h.verify === 'probe'

  async function listo() {
    setBusy(true); setError(null)
    try {
      const updated = await goalService.updateActionable(actionable.actionable_id, { status: 'active' })
      trackAppEvent('hygiene_listo', { check_id: h!.check_id, items: h!.items_total })
      onChange?.({ ...actionable, ...updated, hygiene: updated.hygiene || h })
    } catch (e: any) {
      setError('No se pudo guardar. Probá de nuevo en un rato.')
    } finally {
      setBusy(false)
    }
  }

  async function noMeInteresa() {
    setBusy(true); setError(null)
    try {
      await goalService.dismissActionable(actionable.actionable_id)
      trackAppEvent('hygiene_no_me_interesa', { check_id: h!.check_id })
      onChange?.({ ...actionable, status: 'dismissed' })
    } catch (e: any) {
      setError('No se pudo guardar. Probá de nuevo en un rato.')
    } finally {
      setBusy(false)
    }
  }

  // --- read-back states -------------------------------------------------------------
  if (actionable.status === 'done') {
    const byCatalog = h.verified_by === 'catalog' || h.resolved_by === 'catalog'
    const fixedByProbe = h.verified_by === 'probe' || h.resolved_by === 'probe'
    const trafficStopped = h.verified_by === 'traffic_stopped' || h.resolved_by === 'traffic_stopped'
    const on = h.verified_at ? ` el ${fmtDate(h.verified_at)}` : ''
    return (
      <Card>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Tag appearance={trafficStopped ? 'neutral' : 'success'}>{trafficStopped ? 'Cerrado' : 'Hecho'}</Tag>
            <Text fontWeight="medium">{actionable.name}</Text>
          </div>
          <Text fontSize="caption" color="neutral-textLow">
            {fixedByProbe
              ? `Confirmado${on}: las páginas vuelven a responder.`
              : trafficStopped
                ? `Esas páginas dejaron de recibir visitas de campañas${on}. Siguen sin existir: si vuelven a recibir visitas, aparecen de nuevo acá.`
                : byCatalog
                  ? `Confirmado en tu catálogo${on}.`
                  : 'Marcado como hecho. Este cambio no se verifica automáticamente.'}
          </Text>
        </div>
      </Card>
    )
  }

  if (actionable.status === 'active') {
    const pending = typeof h.remaining === 'number' && h.remaining > 0
    return (
      <Card>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Tag appearance={pending ? 'warning' : 'primary'}>{pending ? 'En revisión' : 'Marcado como hecho'}</Tag>
            <Text fontWeight="medium">{actionable.name}</Text>
          </div>
          <Text fontSize="caption" color="neutral-textLow">
            {pending
              ? byProbe
                ? `Todavía ${h.remaining === 1 ? 'hay 1 página que no existe y recibe' : `hay ${h.remaining} páginas que no existen y reciben`} visitas. Se revisa todos los lunes.`
                : `Todavía ${h.remaining === 1 ? 'queda' : 'quedan'} ${h.remaining} ${itemsWord(h.entidad, h.remaining!)} pendiente${h.remaining === 1 ? '' : 's'} en tu catálogo. Se revisa todos los días.`
              : byProbe
                ? 'Se confirma el lunes, pidiendo cada página.'
                : h.verify === 'catalog'
                  ? 'Se confirma en la próxima revisión de tu catálogo (mañana a la mañana).'
                  : 'Marcado como hecho.'}
          </Text>
          {pending && <ItemList items={shown} more={items.length - shown.length} onMore={() => setShowAll(true)} adminUrl={adminUrl} />}
        </div>
      </Card>
    )
  }

  if (actionable.status !== 'proposed') return null

  // --- served ficha -------------------------------------------------------------------
  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Tag appearance="primary">Esta semana</Tag>
          <Text fontWeight="medium">{actionable.name}</Text>
        </div>
        <Text fontSize="caption" color="neutral-textLow">{actionable.intent}</Text>
        <ItemList items={shown} more={items.length - shown.length} onMore={() => setShowAll(true)} adminUrl={adminUrl} />
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button appearance="primary" onClick={listo} disabled={busy}>Listo, ya lo cambié</Button>
          <Button appearance="transparent" onClick={noMeInteresa} disabled={busy}>No me interesa</Button>
        </div>
        {error && <Text fontSize="caption" color="danger-textLow">{error}</Text>}
        <Text fontSize="caption" color="neutral-textLow">
          {byProbe
            ? 'Cuando lo arregles, se confirma automáticamente pidiendo cada página.'
            : h.verify === 'catalog'
              ? 'Cuando lo cambies, se confirma automáticamente con tu catálogo.'
              : 'Este cambio no se verifica automáticamente: cuando lo hagas, marcalo con el botón.'}
        </Text>
      </div>
    </Card>
  )
}

function ItemList({ items, more, onMore, adminUrl }: {
  items: Array<{ id: string; nombre?: string; dato?: Record<string, unknown> }>
  more: number
  onMore: () => void
  adminUrl: (dato?: Record<string, unknown>) => string | null
}) {
  if (items.length === 0) return null
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {items.map((it) => {
        const url = adminUrl(it.dato)
        const extra = datoLine(it.dato)
        return (
          <li key={it.id} className="flex items-baseline gap-2 text-sm">
            <span aria-hidden>·</span>
            {url ? (
              <a href={url} target="_blank" rel="noreferrer" className="text-[var(--nimbus-colors-primary-textLow)] underline-offset-2 hover:underline">
                {it.nombre || it.id}
              </a>
            ) : (
              <span>{it.nombre || it.id}</span>
            )}
            {extra && <span className="text-[var(--nimbus-colors-neutral-textLow)]">{extra}</span>}
          </li>
        )
      })}
      {more > 0 && (
        <li>
          <button type="button" onClick={onMore} className="bg-transparent p-0 text-sm text-[var(--nimbus-colors-primary-textLow)] underline-offset-2 hover:underline">
            y {more} más
          </button>
        </li>
      )}
    </ul>
  )
}

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })
  } catch {
    return iso.slice(0, 10)
  }
}

/** Pick the ficha to show: the served open one, else the most recent verification read-back. */
export function pickHygieneCard(actionables: Actionable[]): Actionable | null {
  const withH = actionables.filter((a) => a.hygiene)
  const served = withH
    .filter((a) => (a.status === 'proposed' || a.status === 'active') && a.hygiene!.served_at)
    .sort((a, b) => (b.hygiene!.served_at || '').localeCompare(a.hygiene!.served_at || ''))
  if (served[0]) return served[0]
  const recentDone = withH
    .filter((a) => a.status === 'done' && a.hygiene!.served_at && a.hygiene!.verified_at)
    .sort((a, b) => (b.hygiene!.verified_at || '').localeCompare(a.hygiene!.verified_at || ''))
  if (recentDone[0] && Date.now() - new Date(recentDone[0].hygiene!.verified_at!).getTime() < 7 * 86400000) return recentDone[0]
  return null
}
